import { AxiosError } from 'axios';
import { inspectionClient } from '@/config/api';

export interface CreateInspectionReportPayload {
  inspectionItemId: string;
  documentId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
  s3Bucket?: string;
  s3Key?: string;
  s3Region?: string;
  reportData?: any;
  notes?: string;
  isAutomaticReport?: boolean;
}

export interface AttachReportDocumentPayload {
  documentId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
}

export interface InspectionReport {
  id: string;
  inspectionItemId: string;
  documentId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  filePath: string;
  s3Bucket?: string;
  s3Key?: string;
  s3Region?: string;
  reportData?: any;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  version: number;
  notes?: string;
  isAutomaticReport: boolean;
}

export interface ReportDownloadUrl {
  documentId: string;
  presignedUrl: string;
  urlExpiresAt: string;
}

export interface InspectionReportLog {
  id: string;
  inspectionItemId: string;
  inspectionReportId?: string;
  action: 'Created' | 'CorrectionSubmitted' | 'Approved' | 'Rejected' | 'ForwardedToInspector' | 'Viewed' | 'PdfGenerated' | 'Deleted';
  actor: 'Inspector' | 'Provider' | 'Bank' | 'System';
  actorUserId: string;
  actorUserName?: string;
  message: string;
  details?: string;
  reportVersion?: number;
  createdAt: string;
}

export class InspectionReportService {
  async createReport(payload: CreateInspectionReportPayload): Promise<InspectionReport> {
    const response = await inspectionClient.post<InspectionReport>(
      '/api/inspection-reports',
      payload,
      { timeout: 120000 } // 2 minutos
    );
    return response.data;
  }

  async getReportById(reportId: string): Promise<InspectionReport> {
    const response = await inspectionClient.get<InspectionReport>(
      `/api/inspection-reports/${reportId}`
    );
    return response.data;
  }

  async getReportsByInspectionItemId(inspectionItemId: string): Promise<InspectionReport[]> {
    const response = await inspectionClient.get<InspectionReport[]>(
      `/api/inspection-reports/inspection-item/${inspectionItemId}`
    );
    return response.data;
  }

  async getLatestReportByInspectionItemId(
    inspectionItemId: string
  ): Promise<InspectionReport | null> {
    try {
      const response = await inspectionClient.get<InspectionReport>(
        `/api/inspection-reports/inspection-item/${inspectionItemId}/latest`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getLatestEditableReportByInspectionItemId(
    inspectionItemId: string
  ): Promise<InspectionReport | null> {
    try {
      const response = await inspectionClient.get<InspectionReport>(
        `/api/inspection-reports/inspection-item/${inspectionItemId}/latest-editable`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getReportDownloadUrl(reportId: string): Promise<ReportDownloadUrl> {
    const response = await inspectionClient.get<ReportDownloadUrl>(
      `/api/inspection-reports/${reportId}/download-url`
    );
    return response.data;
  }

  async deleteReport(reportId: string): Promise<void> {
    await inspectionClient.delete(`/api/inspection-reports/${reportId}`);
  }

  async attachDocument(reportId: string, payload: AttachReportDocumentPayload): Promise<InspectionReport> {
    const response = await inspectionClient.put<InspectionReport>(
      `/api/inspection-reports/${reportId}/document`,
      payload,
      { timeout: 120000 }
    );
    return response.data;
  }

  async markCorrectionsAsResolved(reportId: string): Promise<void> {
    await inspectionClient.post(`/api/inspection-reports/review/mark-corrections-resolved/${reportId}`);
  }

  async getLogsByInspectionItemId(inspectionItemId: string): Promise<InspectionReportLog[]> {
    const response = await inspectionClient.get<InspectionReportLog[]>(
      `/api/inspection-reports/logs/inspection-item/${inspectionItemId}`
    );
    return response.data;
  }
}

export const inspectionReportService = new InspectionReportService();

/**
 * Escolhe o report a usar como base de edição/correção. Prioriza a última transmissão
 * humana (isAutomaticReport=false); cai pro automático original somente se ainda não
 * houver nenhuma versão revisada. Preserva os ajustes do analista após devolução pelo
 * Salesforce e nos demais fluxos de retorno.
 */
export function pickEditableReport(reports: InspectionReport[]): InspectionReport | undefined {
  if (reports.length === 0) return undefined;
  const nonAuto = [...reports].reverse().find(r => !r.isAutomaticReport);
  return nonAuto ?? reports[reports.length - 1];
}
